import { Box, Button, Flex, Popover, PopoverBody, PopoverContent, PopoverTrigger, Stack, Text } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Clock3 } from 'lucide-react'
import { useEffect } from 'react'
import { useFeatureGate } from '../../../hooks/useFeatureGate'
import { myPermissionsQueryOptions } from '../../../lib/performance/queryOptions'
import { useClockStatus, useLiveSeconds } from './hooks'
import { ClockActions, StateBadge } from './ui'
import { timer } from './time'
import { clockStateTone, clockTones } from './presentation'

// The scoped CSS owns entrance motion and its reduced-motion override.
// Do not leave Chakra's default scale-fade running in parallel.
const clockPopoverMotion = {
  variants: {
    enter: { opacity: 1, transition: { duration: 0 } },
    exit: { opacity: 0, transition: { duration: 0 } },
  },
}

export default function ClockHeader() {
  const { hasFeature, isLoading } = useFeatureGate()
  const { data: permissions } = useQuery(myPermissionsQueryOptions)
  const enabled = !isLoading && hasFeature('time_tracking') && !!permissions?.planning?.includes('clock')
  const query = useClockStatus(enabled)
  const { worked, paused } = useLiveSeconds(query.data, query.dataUpdatedAt)
  const { t } = useTranslation()
  const navigate = useNavigate()
  useEffect(() => {
    if (!enabled || query.data?.state !== 'paused') return
    let title = document.title
    const since = query.data.since
    const elapsedAtReceipt = since ? (Date.parse(query.data.server_now) - Date.parse(since)) / 1000 : 0
    const update = () => {
      if (!document.title.startsWith('⏸ ')) title = document.title
      document.title = `⏸ ${timer(elapsedAtReceipt + (Date.now() - query.dataUpdatedAt) / 1000)} · ${title}`
    }
    update()
    const interval = window.setInterval(update, 1000)
    return () => {
      window.clearInterval(interval)
      if (document.title.startsWith('⏸ ')) document.title = title
    }
  }, [enabled, query.data?.state, query.data?.since, query.dataUpdatedAt])
  if (!enabled || !query.data || query.isError) return null
  const status = query.data
  const tone = clockTones[clockStateTone(status.state)]
  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Button
          size="sm"
          variant="outline"
          borderRadius="full"
          className="aw-clock"
          bg={tone.soft}
          color={tone.ink}
          borderColor={tone.border}
          _hover={{ bg: tone.soft, boxShadow: `0 0 0 3px ${tone.soft}` }}
          minH="38px"
          aria-label={t(`timeClock.${status.state}`)}
          px={3}
        >
          <Flex align="center" gap={2}>
            <Clock3 size={16} />
            <Box
              display={{ base: 'none', lg: 'block' }}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
              minW={status.state === 'off' ? undefined : '65px'}
            >
              {status.state === 'off' ? t('timeClock.in') : timer(status.state === 'paused' ? paused : worked)}
            </Box>
          </Flex>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        motionProps={clockPopoverMotion}
        className="aw-clock"
        maxW="calc(100vw - 24px)"
        borderRadius="20px"
        borderColor="#e9e6f2"
        boxShadow="0 16px 42px -14px rgba(54, 39, 96, .2)"
      >
        <PopoverBody p={4}>
          <Stack spacing={4}>
            <StateBadge state={status.state} />
            <Text fontSize="2xl" fontWeight="700" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {timer(status.state === 'paused' ? paused : worked)}
            </Text>
            <ClockActions status={status} compact />
            <Button
              variant="link"
              size="sm"
              colorScheme="purple"
              onClick={() => navigate('/user/planning/time-tracking?view=clock')}
            >
              {t('timeClock.clock')}
            </Button>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
